/**
 * Mobile header behaviour (views/home/header.php).
 *
 * The whole header is fixed, but only the ad is meant to stay in view:
 * scrolling down slides the masthead and the PILNE ticker up behind it, and
 * scrolling up brings them back — the pattern the review asked for ("tak jak
 * na interii"). The movement itself is CSS (_header.scss); this file only
 * measures the heights, reserves them on <body> and decides the direction.
 *
 * iOS Safari needs three guards that a desktop browser does not:
 *
 *   · rubber-band bounce reports scroll positions above and below the real
 *     range, which reads as a violent direction change;
 *   · the collapsing address bar resizes the viewport and fires scroll
 *     events nobody triggered with a finger;
 *   · momentum scrolling delivers frames unevenly, so a single small delta
 *     must never flip the header.
 *
 * Nothing runs above the breakpoint (the desktop has its own sticky bar).
 */

const DOWN_BEFORE_HIDE = 24; // px of real downward travel before collapsing
const UP_BEFORE_SHOW = 16;   // px upward brings it back
const FREE_ZONE = 80;        // near the top the header always shows in full
const IGNORE_JITTER = 2;     // sub-pixel noise from momentum scrolling
const BOUNCE_ZONE = 40;      // px at the foot of the page where iOS bounces

export function initMobileHeader() {
  const header = document.querySelector('.hp-header');

  if (!header || !window.matchMedia('(max-width: 1259.98px)').matches) {
    return;
  }

  const root = document.documentElement;
  const mast = header.querySelector('.hp-mast');
  const ticker = header.querySelector('.hp-ticker');

  const measure = () => {
    const bar = (mast?.offsetHeight ?? 0) + (ticker?.offsetHeight ?? 0);
    root.style.setProperty('--m-bar-h', `${bar}px`);
  };

  measure();
  header.classList.add('is-sticky');

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return; // fixed, but never sliding
  }

  let last = window.scrollY;
  let viewport = window.innerHeight;
  let travelled = 0;
  let collapsed = false;
  let ticking = false;

  const setCollapsed = (state) => {
    if (collapsed === state) {
      return;
    }
    collapsed = state;
    header.classList.toggle('is-collapsed', state);
  };

  const apply = () => {
    ticking = false;

    const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    const y = window.scrollY;

    // iOS keeps scrollY inside the range and shows the bounce visually, so a
    // reading outside it never arrives — but the ends are still where the
    // bounce happens. Hold the state there rather than react to its wobble.
    if (y < 0 || y > max || y >= max - BOUNCE_ZONE) {
      last = Math.min(Math.max(y, 0), max);
      travelled = 0;
      return;
    }

    // The address bar resized the viewport; the resulting scroll event is
    // the browser's, not the reader's.
    if (window.innerHeight !== viewport) {
      viewport = window.innerHeight;
      last = y;
      travelled = 0;
      return;
    }

    const delta = y - last;
    last = y;

    if (Math.abs(delta) < IGNORE_JITTER) {
      return;
    }

    if (y <= FREE_ZONE) {
      travelled = 0;
      setCollapsed(false);
      return;
    }

    // Travel counts in one direction; a reversal restarts it, so a shaky
    // finger cannot flip the header back and forth.
    travelled = (delta > 0) === (travelled > 0) ? travelled + delta : delta;

    if (!collapsed && travelled > DOWN_BEFORE_HIDE) {
      setCollapsed(true);
    } else if (collapsed && travelled < -UP_BEFORE_SHOW) {
      setCollapsed(false);
    }
  };

  window.addEventListener('scroll', () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(apply);
    }
  }, { passive: true });

  // A resize is two different events. Width changing means rotation: the ad
  // is a different size, so re-measure and start over. Height changing on
  // its own is iOS hiding or showing its address bar mid-scroll — it must
  // not re-open the header, which is what made it keep popping back.
  let width = window.innerWidth;
  let resizing = 0;
  window.addEventListener('resize', () => {
    if (window.innerWidth === width) {
      viewport = window.innerHeight;
      last = window.scrollY;
      travelled = 0;
      return;
    }

    clearTimeout(resizing);
    resizing = setTimeout(() => {
      width = window.innerWidth;
      viewport = window.innerHeight;
      last = window.scrollY;
      travelled = 0;
      setCollapsed(false);
      measure();
    }, 150);
  }, { passive: true });

  // The ad is an image; once it loads the bar sits lower, so the distance it
  // has to travel changes. Keep it in step.
  if ('ResizeObserver' in window) {
    let known = header.offsetHeight;
    new ResizeObserver(() => {
      if (header.offsetHeight !== known) {
        known = header.offsetHeight;
        measure();
      }
    }).observe(header);
  }

  window.addEventListener('pageshow', () => {
    last = window.scrollY;
    viewport = window.innerHeight;
    travelled = 0;
  });
}
